import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
  TextField,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, FileDownload } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import UniversalModal from './UniversalModal';
import {
  exportPeople,
  getPeopleExportFields,
} from '../utils/api';

/** @typedef {import('./types/PeopleExportDialog.js').ExportField} ExportField */
/** @typedef {import('./types/PeopleExportDialog.js').PeopleExportDialogProps} PeopleExportDialogProps */
/** @typedef {import('./types/PeopleExportDialog.js').PeopleExportResponse} PeopleExportResponse */
/** @typedef {import('./types/PeopleExportDialog.js').ExportMeta} ExportMeta */

/**
 * Converts a database field name into a human-readable label.
 *
 * @param {string} fieldName
 * @returns {string}
 */
const formatFieldLabel = (fieldName) => fieldName
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

/**
 * Normalizes API export field metadata into the shape used by the dialog.
 *
 * @param {Array<ExportMeta>} fields
 * @returns {ExportField[]}
 */
const normalizeExportFields = (fields) => (Array.isArray(fields) ? fields : [])
  .filter(({ column_name }) => column_name)
  .map(({ column_name, label, table = 'faces' }) => ({
    field: `${table}.${column_name}`,
    columnName: column_name,
    table,
    label: label || formatFieldLabel(column_name),
  }));

/**
 * Escapes a value for CSV output.
 *
 * @param {unknown} value
 * @returns {string}
 */
const escapeCsv = (value) => {
  const text = String(value ?? '');

  return /[",\n]/.test(text)
    ? `"${text.replace(/"/g, '""')}"`
    : text;
};

/**
 * Creates a safe filename.
 *
 * @param {unknown} value
 * @returns {string}
 */
const safeFileName = (value) => String(value || 'directory_export')
  .trim()
  .replace(/[^a-zA-Z0-9_-]/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '') || 'directory_export';

/**
 * People export dialog.
 *
 * @param {PeopleExportDialogProps} props
 * @returns {React.ReactElement}
 */
const PeopleExportDialog = ({
  open,
  onClose,
  title,
  exportFilters = {},
}) => {
  const suggestedFileName = `${safeFileName(title)}_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}`;

  /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
  const [format, setFormat] = useState('csv');

  /** @type {[ExportField[], React.Dispatch<React.SetStateAction<ExportField[]>>]} */
  const [directoryFields, setDirectoryFields] = useState([]);

  /** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
  const [selectedFields, setSelectedFields] = useState([]);

  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [exporting, setExporting] = useState(false);

  /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
  const [exportFileName, setExportFileName] = useState(suggestedFileName);

  /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
  const [loadingFields, setLoadingFields] = useState(false);

  /**
   * Toggles an export field.
   *
   * @param {string} field
   * @param {boolean} checked
   * @returns {void}
   */
  const toggleField = (field, checked) => {
    setSelectedFields((current) => checked
      ? (current.includes(field) ? current : [...current, field])
      : current.filter((item) => item !== field));
  };

  /**
   * Moves a selected field up or down.
   *
   * @param {number} index
   * @param {'up'|'down'} direction
   * @returns {void}
   */
  const moveField = (index, direction) => {
    setSelectedFields((current) => {
      const next = [...current];
      const target = direction === 'up' ? index - 1 : index + 1;

      if (target < 0 || target >= next.length) {
        return current;
      }

      [next[index], next[target]] = [next[target], next[index]];

      return next;
    });
  };

  /**
   * Exports the selected people fields as CSV or XLSX.
   *
   * @returns {Promise<void>}
   */
  const handleExport = async () => {
    if (!selectedFields.length) {
      return;
    }

    setExporting(true);

    try {
      /** @type {PeopleExportResponse} */
      const response = await exportPeople(selectedFields, exportFilters);

      const rows = Array.isArray(response?.rows)
        ? response.rows
        : [];

      const fields = selectedFields
        .map((field) => directoryFields.find((item) => item.field === field))
        .filter(Boolean);

      const headers = fields.map(({ label }) => label);

      const values = rows.map((row) => (
        fields.map(({ field }) => row[field] ?? '')
      ));

      const fileName = safeFileName(
        exportFileName.trim() || suggestedFileName,
      );

      if (format === 'xlsx') {
        const worksheet = XLSX.utils.aoa_to_sheet([
          headers,
          ...values,
        ]);

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          title.slice(0, 31) || 'Directory',
        );

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      } else {
        const csv = [headers, ...values]
          .map((row) => row.map(escapeCsv).join(','))
          .join('\n');

        const url = window.URL.createObjectURL(
          new Blob([csv], {
            type: 'text/csv;charset=utf-8;',
          }),
        );

        const link = document.createElement('a');

        link.href = url;
        link.download = `${fileName}.csv`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      }

      onClose();
    } finally {
      setExporting(false);
    }
  };

  const orderedFields = [
    ...selectedFields,
    ...directoryFields
      .map(({ field }) => field)
      .filter((field) => !selectedFields.includes(field)),
  ];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    setLoadingFields(true);
    setDirectoryFields([]);
    setSelectedFields([]);

    getPeopleExportFields()
      .then(
        /** @param {Array<ExportMeta>} response */
        (response) => {

        if (cancelled) {
          return;
        }

        const fields = normalizeExportFields(response);

        setDirectoryFields(fields);
        setSelectedFields(fields.map(({ field }) => field));
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(
            'Failed to fetch "/persons/export-meta"',
            error,
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingFields(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, title]);

  return (
    <UniversalModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      title={`Export ${title}`}
      contentDividers
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>

          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleExport}
            disabled={exporting || !selectedFields.length}
          >
            {exporting
              ? 'Exporting...'
              : `Export ${format.toUpperCase()}`}
          </Button>
        </>
      )}
    >
      <Stack spacing={2}>
        <TextField
          label="File name"
          size="small"
          value={exportFileName}
          onChange={(e) => setExportFileName(e.target.value)}
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="directory-export-format-label">
            Format
          </InputLabel>

          <Select
            labelId="directory-export-format-label"
            value={format}
            label="Format"
            onChange={(event) => setFormat(event.target.value)}
          >
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="xlsx">XLSX</MenuItem>
          </Select>
        </FormControl>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {loadingFields
              ? 'Loading columns...'
              : 'Columns (select + order)'}
          </Typography>

          <List
            dense
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {orderedFields.map((fieldName) => {
              const field = directoryFields.find(
                (item) => item.field === fieldName,
              );

              const selected = selectedFields.includes(fieldName);
              const index = selectedFields.indexOf(fieldName);

              if (!field) {
                return null;
              }

              return (
                <ListItem
                  key={fieldName}
                  secondaryAction={selected ? (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => moveField(index, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => moveField(index, 'down')}
                        disabled={index === selectedFields.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Stack>
                  ) : null}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selected}
                      onChange={(event) => toggleField(
                        fieldName,
                        event.target.checked,
                      )}
                    />
                  </ListItemIcon>

                  <ListItemText primary={field.label} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Stack>
    </UniversalModal>
  );
};

export default PeopleExportDialog;
